import { useEffect, useState } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Ban, History } from "lucide-react";
import { errorText, fmtStamp } from "@/lib/calendar";

export const CancelSection = ({ ride, onChanged }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  if (!["requested", "approved", "scheduled"].includes(ride.status)) {
    return <p className="text-sm text-slate-500">Only upcoming rides can be cancelled.</p>;
  }
  const cancel = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/rides/${ride.id}/cancel`, { reason });
      toast.success("Ride cancelled. Removed from active pickups and notifications sent.");
      onChanged();
    } catch (e) {
      toast.error(errorText(e, "Failed to cancel ride"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="cancel-section">
      <Textarea rows={2} placeholder="Reason for cancellation (shared with driver and customer)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" />
      {!confirming ? (
        <Button variant="destructive" onClick={() => setConfirming(true)} data-testid="cancel-ride-btn">
          <Ban className="w-4 h-4 mr-2" />Cancel This Ride
        </Button>
      ) : (
        <div className="rounded-xl border border-red-300 bg-red-50 p-3 space-y-2" data-testid="cancel-confirm-box">
          <p className="text-sm text-red-800 font-semibold">Cancel {ride.client_name}'s ride? The driver and customer will be notified.</p>
          <div className="flex gap-2">
            <Button variant="destructive" onClick={cancel} disabled={busy} data-testid="cancel-confirm-btn">Yes, cancel ride</Button>
            <Button variant="outline" onClick={() => setConfirming(false)} data-testid="cancel-abort-btn">Keep ride</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const fmtChange = ([k, [from, to]]) => `${k.replace(/_/g, " ")}: ${from ?? "—"} → ${to ?? "—"}`;

export const HistoryEntry = ({ h, showClient = false }) => (
  <div className="border-l-2 border-cyan-500 pl-3 py-1" data-testid={`history-entry-${h.id}`}>
    <p className="text-sm font-semibold capitalize">
      {h.action}{showClient && h.client_name ? ` · ${h.client_name}` : ""}
    </p>
    <p className="text-xs text-slate-500">{fmtStamp(h.created_at)} by {h.actor_name}</p>
    {Object.entries(h.changes || {}).map((c) => <p key={c[0]} className="text-xs text-slate-600">{fmtChange(c)}</p>)}
    {h.note && <p className="text-xs italic text-slate-600">{h.note}</p>}
  </div>
);

export const HistorySection = ({ rideId, refreshKey }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    apiClient.get(`/rides/${rideId}/history`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [rideId, refreshKey]);
  return (
    <div className="space-y-2" data-testid="history-section">
      {items.length === 0 && <p className="text-sm text-slate-500 flex items-center gap-2"><History className="w-4 h-4" />No changes recorded yet.</p>}
      {items.map((h) => <HistoryEntry key={h.id} h={h} />)}
    </div>
  );
};
